/**
 * useEcommerceCart - Hook pour la gestion du panier e-commerce
 * Utilise Zustand pour un state management simple et performant
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem, CartItemAddedBy, CartTotals, Product, LotPromoOffre } from '../../types/ecommerce.types';

/**
 * Clé d'une ligne du panier. Un même produit peut y figurer plusieurs fois : au prix
 * normal, en offre anti-gaspi d'un lot (`|lotId`), et — pour le pain — coupé ou non
 * (`|coupé`). Le seul identifiant du produit désigne la ligne normale non préparée :
 * les appelants historiques (live, fiche, cartes) qui passent un identifiant de
 * produit visent donc toujours celle-là.
 */
export function cleArticle(item: {
  product: { id: string };
  lot?: { lot_id: string } | null;
  prep_options?: string[] | null;
}): string {
  const base = item.lot ? `${item.product.id}|${item.lot.lot_id}` : item.product.id;
  return memesOptions(item.prep_options, null) ? base : `${base}|${[...(item.prep_options || [])].sort().join('+')}`;
}

/** Deux listes d'options de préparation désignent-elles la même préparation ? */
function memesOptions(a?: string[] | null, b?: string[] | null): boolean {
  const x = [...(a || [])].sort().join('+');
  const y = [...(b || [])].sort().join('+');
  return x === y;
}
const correspond = (item: CartItem, cle: string) => cleArticle(item) === cle;
import { logger } from '../../utils/logger';
import { vatRateOf } from '../../utils/pos.utils';
import { consignesDuPanier } from '../../utils/consignesPanier';

interface AddItemOptions {
  quantity?: number;
  addedBy?: CartItemAddedBy;  // 'customer' | 'vendor'
  weightGrams?: number;        // Poids en grammes pour produits is_weighted
  quantityUnit?: 'grams' | 'pieces'; // Comment le client a commandé
  pieceCount?: number;         // Nombre de pièces (si quantityUnit === 'pieces')
  lot?: LotPromoOffre;         // Offre anti-gaspi d'un lot choisie par le client
  prepOptions?: string[];      // Pain : ['coupé'] — une ligne distincte du même pain non coupé
}

interface EcommerceCartState {
  items: CartItem[];
  /**
   * Vidanges que le client rapportera au retrait : { id fiche consigne → nombre }.
   * Vit avec le panier (choisies dans le panier, reprises au checkout, effacées
   * avec lui après la commande) — crédit FOLIES après recomptage au comptoir.
   */
  vidanges: Record<string, number>;
  setVidanges: (v: Record<string, number>) => void;
  /**
   * Bacs Folies vides que le client rapporte au retrait : échangés un pour un
   * contre les bacs pleins de sa commande (CGV art. 9). Vit avec le panier.
   */
  bacs: number;
  setBacs: (n: number) => void;

  // Actions
  addItem: (product: Product, options?: AddItemOptions | number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  // Rechoisir le poids ou le nombre de pièces d'une ligne (édition du panier)
  updateSelection: (productId: string, selection: { weightGrams: number; quantityUnit: 'grams' | 'pieces'; pieceCount?: number }) => void;
  clearCart: () => void;
  forceFullClear: () => void; // Vider complètement le panier (après checkout)
  markAsPrepared: (productId: string, vendorId?: string) => void;
  // Recopier la consigne du catalogue du jour sur les articles déjà au panier
  rafraichirConsignes: (catalogue: Product[]) => void;

  // Computed
  getTotals: () => CartTotals;
  getItemCount: () => number;
  getTotalQuantity: () => number;
  getPreparedCount: () => number;
  getUnpreparedCount: () => number;
  isItemLocked: (productId: string) => boolean;
}

/**
 * Calculer le taux de TVA basé sur la classe fiscale du produit
 */
function getTaxRate(product: Product): number {
  return vatRateOf(product.tax_class);
}

/**
 * Formater un montant en cents vers euros
 */
function formatCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

export const useEcommerceCart = create<EcommerceCartState>()(
  persist(
    (set, get) => ({
      items: [],
      vidanges: {},
      setVidanges: (v) => set({ vidanges: v }),
      bacs: 0,
      setBacs: (n) => set({ bacs: Math.max(0, Math.min(20, Math.floor(n) || 0)) }),

      /**
       * Ajouter un produit au panier
       * @param product - Le produit à ajouter
       * @param options - Quantité et/ou addedBy ('customer' | 'vendor')
       *                  Pour compatibilité, accepte aussi un nombre (quantité)
       */
      addItem: (product: Product, options?: AddItemOptions | number) => {
        // Normaliser les options (rétrocompatibilité avec l'ancienne signature)
        const opts: AddItemOptions = typeof options === 'number'
          ? { quantity: options, addedBy: 'customer' }
          : { quantity: 1, addedBy: 'customer', ...options };

        const quantity = opts.quantity || 1;
        const addedBy = opts.addedBy || 'customer';
        const weightGrams = opts.weightGrams;
        const quantityUnit = opts.quantityUnit;
        const pieceCount = opts.pieceCount;
        // Si ajouté par le vendeur → préparé, sinon non préparé
        const prepared = addedBy === 'vendor';

        const items = get().items;
        // Identifier les items par (product_id, prepared) pour permettre des lignes séparées
        // si le même produit est ajouté dans des contextes différents (vendor vs customer)
        const lot = opts.lot;
        // Seules les options que la fiche propose sont gardées (le serveur refait le tri).
        const prepOptions = (opts.prepOptions || []).filter((o) => (product.options_preparation || []).includes(o));
        const existingItemIndex = items.findIndex((item) =>
          item.product.id === product.id &&
          item.prepared === prepared &&
          (item.lot?.lot_id ?? null) === (lot?.lot_id ?? null) &&
          memesOptions(item.prep_options, prepOptions)
        );

        // Récupérer le prix EUR
        const priceEur = product.prices.find((p) => p.currency === 'EUR');
        if (!priceEur) {
          logger.error('Prix EUR non trouvé pour le produit:', product.name);
          return;
        }

        // Offre anti-gaspi : le prix du LOT (le serveur le recalcule de toute façon).
        const unit_price_cents = lot ? lot.price_cents : priceEur.amount_cents;

        // Calculer le total selon le type de produit et qui l'ajoute
        // IMPORTANT: Vérifier explicitement === true car is_weighted peut être undefined
        const isWeightedProduct = product.is_weighted === true;

        logger.log('[Cart] addItem:', product.name, {
          is_weighted: product.is_weighted,
          isWeightedProduct,
          quantity,
          weightGrams,
          addedBy,
          unit_price_cents,
        });

        const calculateTotalPriceCents = (qty: number, weight?: number) => {
          // Vente à la pièce : prix = (prix_au_kg / 1000) * poids_estimé_total
          if (quantityUnit === 'pieces' && weight) {
            // weight = poids estimé d'UN lot de pièces ; qty = nombre de lots
            // (re-cliquer « ajouter » sur la même fiche en ajoute un).
            const total = Math.round((unit_price_cents / 1000) * weight * Math.max(1, qty));
            logger.log('[Cart] Piece-count product:', { unit_price_cents, weight, pieceCount, total });
            return total;
          }

          if (isWeightedProduct) {
            if (addedBy === 'customer' && weight) {
              // Client : weight = poids unitaire choisi (250g, 500g, 1kg)
              // qty = nombre d'unités de ce poids
              // Prix = (prix_au_kg / 1000) * poids_unitaire * quantité
              const total = Math.round((unit_price_cents / 1000) * weight * qty);
              logger.log('[Cart] Customer weighted product:', { unit_price_cents, weight, qty, total });
              return total;
            } else if (addedBy === 'vendor' && qty) {
              // Vendeur en Live Shopping : qty = grammes exacts pesés
              const total = Math.round((unit_price_cents / 1000) * qty);
              logger.log('[Cart] Vendor weighted product:', { unit_price_cents, qty, total });
              return total;
            }
            // Fallback : traiter comme vendor (qty en grammes)
            return Math.round((unit_price_cents / 1000) * qty);
          }
          // Pour les produits à l'unité : prix unitaire x quantité
          return Math.round(unit_price_cents * qty);
        };

        if (existingItemIndex >= 0) {
          // Produit déjà dans le panier : augmenter la quantité
          const updatedItems = [...items];
          const existingItem = updatedItems[existingItemIndex];
          const newQty = existingItem.quantity + quantity;
          updatedItems[existingItemIndex] = {
            ...existingItem,
            quantity: newQty,
            total_price_cents: calculateTotalPriceCents(newQty, existingItem.weight_grams),
            // Si le vendeur ajoute à un article existant, le marquer comme préparé
            prepared: existingItem.prepared || prepared,
          };
          set({ items: updatedItems });
        } else {
          // Nouveau produit : ajouter au panier
          const newItem: CartItem = {
            product,
            quantity,
            unit_price_cents,
            total_price_cents: calculateTotalPriceCents(quantity, weightGrams),
            prepared,
            addedBy,
            addedAt: new Date().toISOString(),
            ...(weightGrams && { weight_grams: weightGrams }),
            ...(quantityUnit && { quantity_unit: quantityUnit }),
            ...(pieceCount && { piece_count: pieceCount }),
            ...(lot && { lot }),
            ...(prepOptions.length && { prep_options: prepOptions }),
          };
          set({ items: [...items, newItem] });
        }
      },

      /**
       * Supprimer un produit du panier
       * Note: Les articles AJOUTÉS par le vendeur ne peuvent pas être supprimés
       * Les articles ajoutés par le client (même préparés) restent modifiables
       */
      removeItem: (productId: string) => {
        const items = get().items;
        const itemToRemove = items.find((item) => correspond(item, productId));

        // Empêcher la suppression UNIQUEMENT des articles ajoutés par le vendeur
        if (itemToRemove?.addedBy === 'vendor') {
          logger.warn('[Cart] Cannot remove vendor-added item:', productId);
          return;
        }

        set({ items: items.filter((item) => !correspond(item, productId)) });
      },

      /**
       * Mettre à jour la quantité d'un produit
       * Note: Les articles AJOUTÉS par le vendeur ne peuvent pas être modifiés
       * Les articles ajoutés par le client (même préparés) restent modifiables
       */
      updateQuantity: (productId: string, quantity: number) => {
        const items = get().items;
        const itemToUpdate = items.find((item) => correspond(item, productId));

        // Empêcher la modification UNIQUEMENT des articles ajoutés par le vendeur
        if (itemToUpdate?.addedBy === 'vendor') {
          logger.warn('[Cart] Cannot modify vendor-added item:', productId);
          return;
        }

        if (quantity <= 0) {
          get().removeItem(productId);
          return;
        }

        const updatedItems = items.map((item) => {
          if (correspond(item, productId)) {
            // Calcul du prix total selon que le produit est au poids ou non
            // IMPORTANT: Vérifier explicitement === true car is_weighted peut être undefined
            const isWeightedProduct = item.product.is_weighted === true;
            let totalCents: number;

            if (isWeightedProduct) {
              if (item.addedBy === 'customer' && item.weight_grams) {
                // Client : quantity = nb d'unités, weight_grams = poids unitaire
                totalCents = Math.round((item.unit_price_cents / 1000) * item.weight_grams * quantity);
              } else {
                // Vendeur : quantity = grammes exacts
                totalCents = Math.round((item.unit_price_cents / 1000) * quantity);
              }
            } else {
              // Produit à l'unité
              totalCents = Math.round(item.unit_price_cents * quantity);
            }

            return {
              ...item,
              quantity,
              total_price_cents: totalCents,
            };
          }
          return item;
        });
        set({ items: updatedItems });
      },

      /**
       * Rechoisir le poids / le nombre de pièces d'une ligne au poids.
       * La sélection remplace la ligne (quantité ramenée à 1 portion du
       * nouveau poids) ; le total est recalculé au prix du kilo.
       */
      updateSelection: (productId, selection) => {
        const items = get().items;
        const item = items.find((i) => correspond(i, productId));
        if (!item) return;
        if (item.addedBy === 'vendor') {
          logger.warn('[Cart] Cannot modify vendor-added item:', productId);
          return;
        }
        const weightGrams = Math.max(0, Math.round(selection.weightGrams));
        if (weightGrams <= 0) {
          get().removeItem(productId);
          return;
        }
        set({
          items: items.map((i) =>
            correspond(i, productId)
              ? {
                  ...i,
                  quantity: 1,
                  weight_grams: weightGrams,
                  quantity_unit: selection.quantityUnit,
                  piece_count: selection.quantityUnit === 'pieces' ? selection.pieceCount : undefined,
                  total_price_cents: Math.round((i.unit_price_cents / 1000) * weightGrams),
                }
              : i,
          ),
        });
      },

      /**
       * Vider le panier (bouton "vider mes ajouts")
       * Note: Les articles AJOUTÉS par le vendeur sont conservés
       */
      clearCart: () => {
        const items = get().items;
        // Conserver uniquement les articles ajoutés par le vendeur
        const vendorItems = items.filter((item) => item.addedBy === 'vendor');
        set({ items: vendorItems });
      },

      /**
       * Vider complètement le panier (après checkout réussi)
       * Supprime TOUS les articles, y compris ceux du vendeur
       */
      forceFullClear: () => {
        set({ items: [], vidanges: {}, bacs: 0 });
      },

      /**
       * Vérifier si un article est verrouillé (AJOUTÉ par le vendeur)
       * Les articles ajoutés par le client ne sont jamais verrouillés
       */
      isItemLocked: (productId: string) => {
        const items = get().items;
        const item = items.find((i) => correspond(i, productId));
        // Verrouillé UNIQUEMENT si ajouté par le vendeur
        return item?.addedBy === 'vendor';
      },

      /**
       * Calculer les totaux du panier
       */
      getTotals: (): CartTotals => {
        const items = get().items;

        let subtotal_ht_cents = 0;
        let tax_total_cents = 0;

        items.forEach((item) => {
          const taxRate = getTaxRate(item.product);
          const line_total_ttc = item.total_price_cents;

          // Calcul HT et TVA
          const line_total_ht = Math.round(line_total_ttc / (1 + taxRate));
          const line_tax = line_total_ttc - line_total_ht;

          subtotal_ht_cents += line_total_ht;
          tax_total_cents += line_tax;
        });

        // Consignes : hors TVA (0 %), comptées dans le total comme le fait la
        // commande côté serveur — sinon le complément FOLIES proposé serait
        // trop court et la commande refusée après la recharge.
        const deposit_lines = consignesDuPanier(items);
        const deposit_total_cents = deposit_lines.reduce((s, l) => s + l.total_cents, 0);
        subtotal_ht_cents += deposit_total_cents;

        const total_ttc_cents = subtotal_ht_cents + tax_total_cents;

        return {
          subtotal_ht_cents,
          subtotal_ht_euros: formatCents(subtotal_ht_cents),
          tax_total_cents,
          tax_total_euros: formatCents(tax_total_cents),
          total_ttc_cents,
          total_ttc_euros: formatCents(total_ttc_cents),
          item_count: items.length,
          total_quantity: items.reduce((sum, item) => sum + item.quantity, 0),
          deposit_total_cents,
          deposit_lines,
        };
      },

      /**
       * Nombre d'articles distincts
       */
      getItemCount: () => {
        return get().items.length;
      },

      /**
       * Quantité totale d'articles
       */
      getTotalQuantity: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0);
      },

      /**
       * Marquer un article comme préparé (par le vendeur)
       */
      markAsPrepared: (productId: string) => {
        const items = get().items;
        const updatedItems = items.map((item) => {
          if (item.product.id === productId) {
            return {
              ...item,
              prepared: true,
            };
          }
          return item;
        });
        set({ items: updatedItems });
      },

      /**
       * Le panier est gardé en localStorage avec la fiche produit du moment de
       * l'ajout : un article ajouté avant la mise en place des consignes (ou
       * avant un changement de prix de la consigne) n'en porterait pas. Seuls
       * les champs de consigne sont recopiés — le reste ne change pas en silence.
       */
      rafraichirConsignes: (catalogue: Product[]) => {
        const parId = new Map(catalogue.map((p) => [p.id, p]));
        let change = false;
        const items = get().items.map((item) => {
          const frais = parId.get(item.product.id);
          if (!frais) return item;
          if (frais.deposit_cents === item.product.deposit_cents && frais.deposit_name === item.product.deposit_name) return item;
          change = true;
          return { ...item, product: { ...item.product, deposit_cents: frais.deposit_cents, deposit_name: frais.deposit_name } };
        });
        if (change) set({ items });
      },

      /**
       * Nombre d'articles préparés
       */
      getPreparedCount: () => {
        return get().items.filter((item) => item.prepared).length;
      },

      /**
       * Nombre d'articles non préparés
       */
      getUnpreparedCount: () => {
        return get().items.filter((item) => !item.prepared).length;
      },
    }),
    {
      name: 'ecommerce-cart-storage', // Clé localStorage
      partialize: (state) => ({ items: state.items, vidanges: state.vidanges, bacs: state.bacs }), // Articles + vidanges et bacs annoncés
    }
  )
);
